import { Injectable, Inject, Optional } from "@angular/core";
import { API_BASE_URL, FileModel } from "./service-proxies";
import {
  mergeMap as _observableMergeMap,
  catchError as _observableCatch,
} from "rxjs/operators";
import {
  Observable,
  throwError as _observableThrow,
  of as _observableOf,
} from "rxjs";
import {
  HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { TicketFileModel } from "./ticketing-service-proxies";

@Injectable()
export class EmailServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  /**
   * @param id (optional)
   * @return Success
   */
  get(id: string | undefined, UserId: number): Observable<EmailDetailDto> {
    let url_ = this.baseUrl + "/api/services/app/AdministrationService/GetEmailDetail?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "UniqueId=" + encodeURIComponent("" + id) + "&";

      url_ += "UserId=" + encodeURIComponent("" + UserId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGet(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGet(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<EmailDetailDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<EmailDetailDto>;
        })
      );
  }

  protected processGet(response: HttpResponseBase): Observable<EmailDetailDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = EmailDetailDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  getSentEmail(id: number): Observable<SentEmailDto> {
    let url_ = this.baseUrl + "/api/services/app/SentEmail/Get?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";

    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetSentEmail(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetSentEmail(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<SentEmailDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<SentEmailDto>;
        })
      );
  }

  protected processGetSentEmail(response: HttpResponseBase): Observable<SentEmailDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = SentEmailDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param keyword (optional)
   * @param isActive (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getAll(
    keyword: string | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
    userId: number,
    MailBoxType: number
  ): Observable<EmailDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/AdministrationService/GetEmails?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";

      url_ += "userId=" + encodeURIComponent("" + userId) + "&";
      url_ += "MailBoxType=" + encodeURIComponent("" + MailBoxType) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<EmailDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<EmailDtoPagedResultDto>;
        })
      );
  }


  protected processGetAll(
    response: HttpResponseBase
  ): Observable<EmailDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = EmailDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  SendUserTicket(
    body: CreateSentEmailDto | undefined
  ): Observable<boolean> {
    let url_ = this.baseUrl + "/api/services/app/SentEmail/SendEmail";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processSendEmail(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processSendEmail(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<boolean>;
            }
          } else
            return _observableThrow(response_) as any as Observable<boolean>;
        })
      );
  }

  protected processSendEmail(
    response: HttpResponseBase
  ): Observable<boolean> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200;
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  protected processgetSentEmailFiles(
    response: HttpResponseBase
  ): Observable<TicketFileModel> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200;
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }
  getSentEmailFiles(
    fileId: number
  ): Observable<TicketFileModel> {
    let url_ =
      this.baseUrl + "/api/services/app/SentEmail/GetEmailFile?";
    if (fileId === null) {
      throw new Error("The parameter 'fileId' cannot be null.");
    } else if (fileId !== undefined) {
      url_ += "id=" + encodeURIComponent("" + fileId) + "&";
    }
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processgetSentEmailFiles(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processgetSentEmailFiles(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<TicketFileModel>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<TicketFileModel>;
        })
      );
  }
}

export class EmailDto implements IEmailDto {
    emailId: string;
    sender: string;
    subject: string;
    sendTime: string;
    status: boolean;

  constructor(data?: IEmailDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.emailId = _data["emailId"];
      this.sender = _data["sender"];
      this.subject = _data["subject"];
      this.sendTime = _data["sendTime"];
      this.status = _data["status"];
    }
  }

  static fromJS(data: any): EmailDto {
    data = typeof data === "object" ? data : {};
    let result = new EmailDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["emailId"] = this.emailId;
    data["sender"] = this.sender;
    data["subject"] = this.subject;
    data["sendTime"] = this.sendTime;
    data["status"] = this.status;
    return data;
  }

  clone(): EmailDto {
    const json = this.toJSON();
    let result = new EmailDto();
    result.init(json);
    return result;
  }
}

export interface IEmailDto {
  emailId: string;
  sender: string;
  subject: string;
  sendTime: string;
  status: boolean;
}

export class SentEmailDto implements ISentEmailDto {
  id: number;
  ownerUserId: number;
  receiverEmailAddress: string;
  subject: string;
  sendDate: string;
  context: string;
  fileIds: number[];

  constructor(data?: SentEmailDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.receiverEmailAddress = _data["receiverEmailAddress"];
      this.subject = _data["subject"];
      this.sendDate = _data["sendDate"];
      this.context = _data["context"];
      if (Array.isArray(_data["fileIds"])) {
        this.fileIds = [] as any;
        for (let item of _data["fileIds"]) {
          this.fileIds.push(item);
        }
      }
    }
  }

  static fromJS(data: any): SentEmailDto {
    data = typeof data === "object" ? data : {};
    let result = new SentEmailDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["receiverEmailAddress"] = this.receiverEmailAddress;
    data["subject"] = this.subject;
    data["sendDate"] = this.sendDate;
    data["context"] = this.context;

    if (Array.isArray(this.fileIds)) {
      data["fileIds"] = [];
      for (let item of this.fileIds) data["fileIds"].push(item);
    }

    return data;
  }

  clone(): SentEmailDto {
    const json = this.toJSON();
    let result = new SentEmailDto();
    result.init(json);
    return result;
  }
}

export interface ISentEmailDto {
  id: number;
  ownerUserId: number;
  receiverEmailAddress: string;
  subject: string;
  sendDate: string;
  context: string;
  fileIds: number[];
}

export class CreateSentEmailDto implements ICreateSentEmailDto {
  receiver: string;
  ownerUserId: number;
  senderEmailAddr: string;
  subject: string;
  context: string;
  files: TicketFileModel[];
  replyToEmailId: string;
  replyToEmailInfo: string;

  constructor(data?: IEmailDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.receiver = _data["receiver"];
      this.senderEmailAddr = _data["senderEmailAddr"];
      this.ownerUserId = _data["ownerUserId"];
      this.subject = _data["subject"];
      this.context = _data["context"];
      this.replyToEmailId = _data["replyToEmailId"];
      this.replyToEmailInfo = _data["replyToEmailInfo"];
      if (Array.isArray(_data["files"])) {
        this.files = [] as any;
        for (let item of _data["files"]) {
          var model = new TicketFileModel();
          model.init(item);
          this.files.push(model);
        }
      }
    }
  }

  static fromJS(data: any): EmailDto {
    data = typeof data === "object" ? data : {};
    let result = new EmailDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["receiver"] = this.receiver;
    data["senderEmailAddr"] = this.senderEmailAddr;
    data["ownerUserId"] = this.ownerUserId;
    data["subject"] = this.subject;
    data["context"] = this.context;
    data["replyToEmailId"] = this.replyToEmailId;
    data["replyToEmailInfo"] = this.replyToEmailInfo;
    if (Array.isArray(this.files)) {
      data["files"] = [];
      for (let item of this.files) data["files"].push(item);
    }
    return data;
  }

  clone(): EmailDto {
    const json = this.toJSON();
    let result = new EmailDto();
    result.init(json);
    return result;
  }
}

export interface ICreateSentEmailDto {
  receiver: string;
  senderEmailAddr: string;
  ownerUserId: number;
  subject: string;
  context: string;
  replyToEmailId: string;
  files: TicketFileModel[];
}

export class EmailDetailDto implements IEmailDetailDto {
  uniqueId: string;
  from: string;
  senderEmailAddr: string;
  subject: string;
  sendTime: string;
  body: string;
  bodyHtml: string;
  attachements: FileModel[];

  constructor(data?: IEmailDetailDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.uniqueId = _data["uniqueId"];
      this.from = _data["from"];
      this.subject = _data["subject"];
      this.sendTime = _data["sendTime"];
      this.body = _data["body"];
      this.bodyHtml = _data["bodyHtml"];
      this.senderEmailAddr = _data["senderEmailAddr"];
      if (Array.isArray(_data["attachements"])) {
        this.attachements = [] as any;
        for (let item of _data["attachements"]) {
          var model = new FileModel();
          model.init(item);
          this.attachements.push(model);
        }
      }
    }
  }

  static fromJS(data: any): EmailDetailDto {
    data = typeof data === "object" ? data : {};
    let result = new EmailDetailDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["uniqueId"] = this.uniqueId;
    data["from"] = this.from;
    data["subject"] = this.subject;
    data["sendTime"] = this.sendTime;
    data["body"] = this.body;
    data["bodyHtml"] = this.bodyHtml;
    data["senderEmailAddr"] = this.senderEmailAddr;
    if (Array.isArray(this.attachements)) {
      data["attachements"] = [];
      for (let item of this.attachements) data["attachements"].push(item);
    }
    return data;
  }

  clone(): EmailDetailDto {
    const json = this.toJSON();
    let result = new EmailDetailDto();
    result.init(json);
    return result;
  }
}

export interface IEmailDetailDto {
    uniqueId: string;
  from: string;
  subject: string;
  sendTime: string;
  body: string;
  bodyHtml: string;
  senderEmailAddr: string;
}

export class EmailDtoPagedResultDto implements IEmailDtoPagedResultDto {
  items: EmailDto[] | undefined;
  totalCount: number;
  unreadCount: number;
  mailAddr: string;

  constructor(data?: IEmailDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(EmailDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
      this.unreadCount = _data["unreadCount"];
      this.mailAddr = _data["mailAddr"];
    }
  }

  static fromJS(data: any): EmailDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new EmailDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    data["unreadCount"] = this.unreadCount;
    data["mailAddr"] = this.mailAddr;
    return data;
  }

  clone(): EmailDtoPagedResultDto {
    const json = this.toJSON();
    let result = new EmailDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IEmailDtoPagedResultDto {
  items: EmailDto[] | undefined;
  totalCount: number;
  unreadCount: number;
  mailAddr: string;
}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next("");
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = (event) => {
        observer.next((event.target as any).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}

export class ApiException extends Error {
  message: string;
  status: number;
  response: string;
  headers: { [key: string]: any };
  result: any;

  constructor(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result: any
  ) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiException {
    return obj.isApiException === true;
  }
}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any
): Observable<any> {
  if (result !== null && result !== undefined) return _observableThrow(result);
  else
    return _observableThrow(
      new ApiException(message, status, response, headers, null)
    );
}
